import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ErrorBoundary } from 'react-error-boundary';
import './styles/index.js';
import { ROLE_LEVELS } from './utils/roleUtils';

// 🔒 Core components (always loaded) - 핵심 컴포넌트
import NavigationBar from './components/Layout/Navbar';
import AppFooter from './components/Layout/Footer';
import Login from './components/Auth/Login';
import SignUp from './components/Auth/SignUp';
import GlobalProgressBar, { SuspenseProgress } from './components/Common/GlobalProgressBar';
import DSToastHost from './components/Common/DSToastHost';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorPage from './components/ErrorPage/ErrorPage';
import InstructorLayout from './components/Instructor/InstructorLayout';
import AdminLayout from './components/Layout/AdminLayout';
import { trackPageView } from './utils/visitorTracker';
import DemoLayout from './components/Demo/DemoLayout';
import AuthedHome from './components/AuthedHome';

// 🚀 Lazy loaded components - 지연 로딩 컴포넌트
// Auth & Account
const EmailVerification = lazy(() => import('./components/Auth/EmailVerification'));
const AccountManagement = lazy(() => import('./components/Account/AccountManagement'));

// Pages
const Inquiry = lazy(() => import('./components/Inquiry/Inquiry'));
const InstructorApply = lazy(() => import('./components/Recruit/InstructorApply'));
const Policy1 = lazy(() => import('./components/Policy/Policy1'));
const Policy2 = lazy(() => import('./components/Policy/Policy2'));
const NotificationsPage = lazy(() => import('./components/Pages/NotificationsPage'));

// Content
const ContentDetail = lazy(() => import('./components/ContentDetail/ContentDetail'));
const CurriculumDetail = lazy(() => import('./components/CurriculumDetail/CurriculumDetail'));

// Student (무계정 게스트 학생)
const StudentHome = lazy(() => import('./components/Student/StudentHome'));

// Student PBL
const StudentProjectsList = lazy(() => import('./components/Student/StudentProjectsList'));
const StudentAssignmentDetail = lazy(() => import('./components/Student/StudentAssignmentDetail'));
const StudentClassJoin = lazy(() => import('./components/Student/StudentClassJoin'));
const StudentOnboarding = lazy(() => import('./components/Student/StudentOnboarding'));
const StudentPBLWorkspace = lazy(() => import('./components/Student/StudentPBLWorkspace'));
const StudentPortfolio = lazy(() => import('./components/Student/StudentPortfolio'));
// 팀 직업 시뮬레이션 — 학생은 무계정(게스트 토큰)이라 ProtectedRoute 를 쓰지 않는다.
const StudentSimulation = lazy(() => import('./components/Student/StudentSimulation'));
const SimulationDashboard = lazy(() => import('./components/AdminPanel/SimulationDashboard'));
const SimulationDemo = lazy(() => import('./components/Student/SimulationDemo'));
const PublicPortfolioView = lazy(() => import('./components/Portfolio/PublicPortfolioView'));

// Instructor Portal (멘토 포털)
const InstructorDashboard = lazy(() => import('./components/Instructor/InstructorDashboard'));
const InstructorClassList = lazy(() => import('./components/Instructor/InstructorClassList'));
const LeagueWiki = lazy(() => import('./components/Instructor/LeagueWiki/LeagueWiki'));
const InstructorClassDetail = lazy(() => import('./components/Instructor/InstructorClassDetail'));
const InstructorClassSimulation = lazy(() => import('./components/Instructor/InstructorClassSimulation'));
const InstructorRoster = lazy(() => import('./components/Instructor/InstructorRoster'));
const InstructorDeliverables = lazy(() => import('./components/Instructor/InstructorDeliverables'));
const InstructorReflections = lazy(() => import('./components/Instructor/InstructorReflections'));
const InstructorDocumentSchools = lazy(() => import('./components/Instructor/InstructorDocumentSchools'));
const InstructorBoardList = lazy(() => import('./components/Instructor/Board/BoardList'));
const InstructorBoardDetail = lazy(() => import('./components/Instructor/Board/BoardDetail'));

// Admin Panel
const UserManagementLazy = lazy(() => import('./components/AdminPanel/UserManagement'));
const SchoolManagement = lazy(() => import('./components/AdminPanel/SchoolManagement'));
const CostDashboard = lazy(() => import('./components/AdminPanel/CostDashboard'));
const ClassOperationManagement = lazy(() => import('./components/AdminPanel/ClassOperationManagement'));
const ClassOperationDetail = lazy(() => import('./components/AdminPanel/ClassOperationDetail'));
const ClassInquiry = lazy(() => import('./components/AdminPanel/ClassInquiry'));
const CurriculumManagement = lazy(() => import('./components/AdminPanel/CurriculumManagement'));
const InstructorManagement = lazy(() => import('./components/AdminPanel/InstructorManagement'));
const DeletedInstructors = lazy(() => import('./components/AdminPanel/DeletedInstructors'));
const VisitorAnalytics = lazy(() => import('./components/AdminPanel/VisitorAnalytics'));
const InstructorOverview = lazy(() => import('./components/AdminPanel/InstructorOverview'));
const OperationsOverview = lazy(() => import('./components/AdminPanel/OperationsOverview'));
const ReflectionLive = lazy(() => import('./components/AdminPanel/ReflectionLive'));
const SupervisionPage = lazy(() => import('./components/Instructor/SupervisionPage'));
const InstructorDocsPage = lazy(() => import('./components/AdminPanel/InstructorDocsPage'));
const InstructorRecruitment = lazy(() => import('./components/AdminPanel/InstructorRecruitment'));
const InstructorApplicationArchive = lazy(() => import('./components/AdminPanel/InstructorApplicationArchive'));
const ApiDocs = lazy(() => import('./components/AdminPanel/ApiDocs'));
const InstructorCurriculumHistory = lazy(() => import('./components/AdminPanel/InstructorCurriculumHistory'));
const InstructorSettlementSchools = lazy(() => import('./components/AdminPanel/InstructorSettlementSchools'));
const InstructorSettlement = lazy(() => import('./components/AdminPanel/InstructorSettlement'));
const MonthlySettlementAggregate = lazy(() => import('./components/AdminPanel/MonthlySettlementAggregate'));
const McpTokens = lazy(() => import('./components/AdminPanel/McpTokens'));
const OAuthConsent = lazy(() => import('./components/AdminPanel/OAuthConsent'));

// Instructor Recruitment (멘토 모집 게시판)
const RecruitBoard = lazy(() => import('./components/Instructor/RecruitBoard'));
const InstructorCalendar = lazy(() => import('./components/Instructor/InstructorCalendar'));

// 데모 웹사이트 (/demo — 로그인 불필요, 큐레이션 정적 데이터)
const DemoCurriculum = lazy(() => import('./components/Demo/DemoCurriculum'));
const DemoCurriculumDetail = lazy(() => import('./components/Demo/DemoCurriculumDetail'));
const DemoStudentWorks = lazy(() => import('./components/Demo/DemoStudentWorks'));
const DemoSchoolRecord = lazy(() => import('./components/Demo/DemoSchoolRecord'));
const DemoSubmissionExample = lazy(() => import('./components/Demo/DemoSubmissionExample'));

// 같은 경로 조회가 연달아 찍히지 않게(개발 모드 StrictMode 의 이중 effect, 해시 이동 등) 직전 기록을 기억한다.
let lastTrackedView = { path: null, at: 0 };

function AppLayout() {
    const location = useLocation();

    // 방문자 분석 — 공개 화면(랜딩·커리큘럼·문의·로그인·회원가입)의 페이지 조회만 남긴다(utils/visitorTracker).
    useEffect(() => {
        const now = Date.now();
        if (lastTrackedView.path === location.pathname && now - lastTrackedView.at < 2000) return;
        lastTrackedView = { path: location.pathname, at: now };
        trackPageView(location.pathname);
    }, [location.pathname]);
    const isStandaloneLayout = location.pathname === '/login' ||
        location.pathname === '/signup' ||
        location.pathname === '/inquiry' ||
        // 어드민: 자체 사이드바(AdminLayout) → 메인 상단바 숨김
        location.pathname.startsWith('/admin') ||
        // 멘토 포털: 자체 사이드바(InstructorLayout) → 메인 상단바 숨김 (어드민 포함)
        location.pathname.startsWith('/instructor') ||
        // 데모(=메인 온보딩): 자체 헤더(DemoLayout) → 메인 상단바 숨김 (로그인 불필요)
        location.pathname.startsWith('/main') ||
        location.pathname.startsWith('/demo') ||
        // 약관 페이지: 메인과 같은 헤더(DemoLayout) 사용
        location.pathname === '/privacy' ||
        location.pathname === '/terms' ||
        // 무계정 학생 포털(/student/home): 자체 상단바 사용 → 메인 상단바 숨김
        location.pathname.startsWith('/student/') ||
        location.pathname.startsWith('/portfolio/view/');

    return (
        <div className="d-flex flex-column min-vh-100" style={{ paddingTop: !isStandaloneLayout ? '60px' : '0' }}>
            <GlobalProgressBar />
            {!isStandaloneLayout && <NavigationBar />}
            <main className="flex-grow-1">
                <Suspense fallback={<SuspenseProgress />}>
                    <Routes>
                    <Route path="/verify-email/:token" element={<EmailVerification />} />
                    <Route path="/portfolio/view/:shareToken" element={<PublicPortfolioView />} />
                    <Route path="/" element={<AuthedHome />} />
                    <Route path="/privacy" element={<DemoLayout><Policy1 /></DemoLayout>} />
                    <Route path="/terms" element={<DemoLayout><Policy2 /></DemoLayout>} />
                    <Route path="/dashboard" element={
                        <ProtectedRoute>
                            <AuthedHome />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/*" element={
                        <ProtectedRoute minLevel={ROLE_LEVELS.STAFF}>
                            <AdminLayout>
                                {/* Suspense 경계를 레이아웃 안에 둬 청크 로딩 시 사이드바는 유지되고 콘텐츠만 잠깐 빈다 */}
                                <Suspense fallback={<SuspenseProgress />}>
                                    <Routes>
                                        {/* admin과 staff는 관리자 화면에서 완전히 동일한 기능을 쓴다 — 예전
                                            "대시보드"(admin 전용)는 운영 현황에 흡수됐고, 사용자/AI비용/반
                                            관리/게시판 관리도 전부 staff(roleLevel 5)까지 개방했다. 유일한
                                            예외는 "관리자로의 승격"(사용자 관리 화면 안에서 서버가 차단). */}
                                        <Route path="overview" element={<OperationsOverview />} />
                                        <Route path="reflections-live" element={<ReflectionLive />} />
                                        <Route path="users" element={<UserManagementLazy />} />
                                        <Route path="cost" element={<CostDashboard />} />
                                        <Route path="classes" element={<InstructorClassList adminView />} />
                                        <Route path="classes/:classId" element={<InstructorClassDetail adminView />} />
                                        <Route path="classes/:classId/simulation" element={<InstructorClassSimulation adminView />} />
                                        <Route path="classes/:classId/deliverables" element={<InstructorDeliverables adminView />} />
                                        <Route path="board" element={<InstructorBoardList adminView />} />
                                        <Route path="board/:id" element={<InstructorBoardDetail adminView />} />

                                        <Route path="schools" element={<SchoolManagement />} />
                                        <Route path="class-operations" element={<ClassOperationManagement />} />
                                        <Route path="class-inquiry" element={<ClassInquiry />} />
                                        <Route path="class-operations/:id" element={<ClassOperationDetail />} />
                                        <Route path="class-operations/:opId/crew/:instructorId" element={<InstructorDocsPage />} />
                                        <Route path="curriculum" element={<CurriculumManagement />} />
                                        <Route path="instructors" element={<InstructorManagement />} />
                                        <Route path="instructors/deleted" element={<DeletedInstructors />} />
                                        <Route path="visitors" element={<VisitorAnalytics />} />
                                        <Route path="instructor-overview" element={<InstructorOverview />} />
                                        <Route path="instructor-recruitment" element={<InstructorRecruitment />} />
                                        <Route path="instructor-recruitment/archive" element={<InstructorApplicationArchive />} />
                                        {/* 개발자용 API 문서 — 사이드바에 노출 안 함, 릴리즈 빌드(production)에는 라우트 자체를 안 심음 */}
                                        {process.env.NODE_ENV !== 'production' && (
                                            <Route path="api-docs" element={<ApiDocs />} />
                                        )}
                                        <Route path="instructor-history" element={<InstructorCurriculumHistory />} />
                                        <Route path="instructor-settlement" element={<InstructorSettlementSchools />} />
                                        <Route path="instructor-settlement/:classOperationId" element={<InstructorSettlement />} />
                                        {/* 근무 단위(단기: YYYY-MM-DD / 장기: YYYY-MM)별 개별 페이지.
                                            날짜를 안 주고 들어오면 위 라우트가 첫 단위로 리다이렉트한다. */}
                                        <Route path="instructor-settlement/:classOperationId/:unit" element={<InstructorSettlement />} />
                                        <Route path="monthly-settlement" element={<MonthlySettlementAggregate />} />
                                        <Route path="mcp-tokens" element={<McpTokens />} />
                                        <Route path="oauth-consent" element={<OAuthConsent />} />
                                        <Route path="account" element={<AccountManagement />} />
                                    </Routes>
                                </Suspense>
                            </AdminLayout>
                        </ProtectedRoute>
                    } />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<SignUp />} />

                    {/* 무계정 게스트 학생 라우트 (공개 - 인증 불필요) */}
                    {/* 학생 로그인은 /login 화면(학생 모드)으로 통합됨 — 옛 단독 경로는 리다이렉트.
                        /student/home 은 토큰 없으면 컴포넌트가 /login 으로 리다이렉트 */}
                    <Route path="/student-login" element={<Navigate to="/login" replace />} />
                    <Route path="/student/home" element={<StudentHome />} />
                    <Route path="/inquiry" element={<Inquiry />} />
                    {/* 멘토 지원 공개 폼 (무인증) */}
                    <Route path="/instructor-apply" element={<InstructorApply />} />
                    <Route path="/content/:id" element={<ContentDetail />} />
                    <Route path="/curriculum/:type" element={<CurriculumDetail />} />

                    {/* 학생 PBL 라우트 (roleLevel >= 2) */}
                    <Route path="/students/join-class" element={
                        <ProtectedRoute minLevel={ROLE_LEVELS.LEARNER}>
                            <StudentClassJoin />
                        </ProtectedRoute>
                    } />
                    <Route path="/students/projects" element={
                        <ProtectedRoute minLevel={ROLE_LEVELS.LEARNER}>
                            <StudentProjectsList />
                        </ProtectedRoute>
                    } />
                    <Route path="/students/projects/:projectId/onboarding" element={
                        <ProtectedRoute minLevel={ROLE_LEVELS.LEARNER}>
                            <StudentOnboarding />
                        </ProtectedRoute>
                    } />
                    <Route path="/students/projects/:projectId/workspace" element={
                        <ProtectedRoute minLevel={ROLE_LEVELS.LEARNER}>
                            <StudentPBLWorkspace />
                        </ProtectedRoute>
                    } />
                    <Route path="/students/assignments/:assignmentId" element={
                        <ProtectedRoute minLevel={ROLE_LEVELS.LEARNER}>
                            <StudentAssignmentDetail />
                        </ProtectedRoute>
                    } />
                    <Route path="/students/portfolio" element={
                        <ProtectedRoute minLevel={ROLE_LEVELS.LEARNER}>
                            <StudentPortfolio />
                        </ProtectedRoute>
                    } />
                    {/* 학생 시뮬레이션 — 게스트 토큰으로 접근하므로 ProtectedRoute(User 기준)를 쓰지 않는다.
                        토큰이 없으면 컴포넌트가 "진행 중인 시뮬레이션 없음"을 보여준다. */}
                    <Route path="/student/simulation" element={<StudentSimulation />} />
                    {/* 학생 화면 미리보기 — 실제 학생용 컴포넌트를 그대로 렌더링한다.
                        반/팀 없이도 볼 수 있어야 하므로 로그인만 요구한다. */}
                    <Route path="/admin/simulation/demo" element={
                        <ProtectedRoute minLevel={ROLE_LEVELS.TEACHER}>
                            <SimulationDemo />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/simulation" element={
                        <ProtectedRoute minLevel={ROLE_LEVELS.TEACHER}>
                            <SimulationDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/students/mcp-tokens" element={
                        <ProtectedRoute minLevel={ROLE_LEVELS.LEARNER}>
                            <McpTokens />
                        </ProtectedRoute>
                    } />

                    {/* 멘토 포털 라우트 (roleLevel >= 3) — 단일 InstructorLayout + 중첩 Routes.
                        페이지 이동 시 레이아웃(사이드바)을 재마운트하지 않아 깜빡임이 없다. */}
                    <Route path="/instructor/*" element={
                        <ProtectedRoute minLevel={ROLE_LEVELS.TEACHER}>
                            <InstructorLayout>
                                {/* Suspense 경계를 레이아웃 안에 둬 청크 로딩 시 사이드바는 유지되고 콘텐츠만 잠깐 빈다 */}
                                <Suspense fallback={<SuspenseProgress />}>
                                    <Routes>
                                        <Route path="dashboard" element={<InstructorDashboard />} />
                                        <Route path="classes" element={<InstructorClassList />} />
                                        <Route path="classes/:classId" element={<InstructorClassDetail />} />
                                        <Route path="classes/:classId/roster" element={<InstructorRoster />} />
                                        <Route path="classes/:classId/simulation" element={<InstructorClassSimulation />} />
                                        <Route path="classes/:classId/deliverables" element={<InstructorDeliverables />} />
                                        <Route path="classes/:classId/reflections" element={<InstructorReflections />} />
                                        <Route path="documents" element={<InstructorDocumentSchools />} />
                                        <Route path="documents/:opId" element={<InstructorDocsPage selfMode />} />
                                        <Route path="recruitment" element={<RecruitBoard />} />
                                        <Route path="calendar" element={<InstructorCalendar />} />
                                        {/* 리그 위키 — 점수 규칙·전략·정답 관련 정보가 있어 멘토 포털(강사·스태프·관리자)에만 둔다 */}
                                        <Route path="league-wiki" element={<LeagueWiki />} />
                                        <Route path="supervision" element={<SupervisionPage />} />
                                        <Route path="board" element={<InstructorBoardList />} />
                                        <Route path="board/:id" element={<InstructorBoardDetail />} />
                                        <Route path="account" element={<AccountManagement />} />
                                        <Route path="mcp-tokens" element={<McpTokens />} />
                                    </Routes>
                                </Suspense>
                            </InstructorLayout>
                        </ProtectedRoute>
                    } />

                    {/* 메인 온보딩(=데모) — 공개(로그인 불필요). /main 이 곧 메인 화면(커리큘럼). */}
                    <Route path="/main/*" element={
                        <DemoLayout>
                            <Suspense fallback={<SuspenseProgress />}>
                                <Routes>
                                    <Route index element={<DemoCurriculum />} />
                                    <Route path="curriculum/:slug" element={<DemoCurriculumDetail />} />
                                    <Route path="student-works" element={<DemoStudentWorks />} />
                                    <Route path="school-record" element={<DemoSchoolRecord />} />
                                    <Route path="submission-example" element={<DemoSubmissionExample />} />
                                </Routes>
                            </Suspense>
                        </DemoLayout>
                    } />
                    {/* 구 경로 호환: /demo/* → /main */}
                    <Route path="/demo/*" element={<Navigate to="/main" replace />} />

                    {/* 알림 페이지 */}
                    <Route path="/notifications" element={
                        <ProtectedRoute minLevel={ROLE_LEVELS.LEARNER}>
                            <NotificationsPage />
                        </ProtectedRoute>
                    } />

                    <Route path="*" element={<ErrorPage />} />
                    </Routes>
                </Suspense>
            </main>
            {!isStandaloneLayout && <AppFooter />}
        </div>
    );
}

// 전역 에러 폴백 — fallback 이 없으면 react-error-boundary 는 크래시 시 아무것도 렌더하지 않아
// '하얀 화면'이 된다. 친절한 안내 + 새로고침(예전 빌드의 청크 오류도 새로고침으로 복구) + 원인 메시지 표시.
function AppErrorFallback({ error }) {
    const msg = String((error && (error.message || error.toString())) || '알 수 없는 오류');
    return (
        <div style={{
            minHeight: '100vh', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', padding: '40px 20px', textAlign: 'center'
        }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ margin: '0 0 8px' }}>일시적인 오류가 발생했어요</h2>
            <p style={{ color: '#666', margin: '0 0 24px', maxWidth: 520 }}>
                아래 <b>새로고침</b> 버튼을 눌러 다시 시도해 주세요. 문제가 계속되면 아래 메시지를 선생님께 보여주세요.
            </p>
            <button
                onClick={() => { try { sessionStorage.clear(); } catch { /* ignore */ } window.location.reload(); }}
                style={{ padding: '12px 28px', fontSize: 16, fontWeight: 600, borderRadius: 10, border: 'none', background: '#6c5ce7', color: '#fff', cursor: 'pointer' }}
            >
                새로고침
            </button>
            <pre style={{
                marginTop: 24, maxWidth: 640, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                fontSize: 12, color: '#999', background: '#f5f5f7', padding: '10px 14px', borderRadius: 8
            }}>
                {msg}
            </pre>
        </div>
    );
}

function App() {
    return (
        <ErrorBoundary fallbackRender={(props) => <AppErrorFallback {...props} />}>
            <AuthProvider>
                <NotificationProvider>
                    <Router>
                        <AppLayout />
                    </Router>
                    {/* 전역 DS 토스트 — 우측 상단. 앱 곳곳의 toast.success/error 호출이 여기로 표시됨 */}
                    <DSToastHost />
                </NotificationProvider>
            </AuthProvider>
        </ErrorBoundary>
    );
}

export default App;
